// ============================================================
// Profileur : géométrie des courbes (sans React, sans DOM)
// ============================================================
// Fonctions pures (testées par profiler.chart.test.ts). Les composants ne font que dessiner ce
// qu'elles calculent : échelles, graduations rondes, tracés qui s'interrompent là où une mesure
// manque (un trou n'est jamais relié, ni ramené à zéro).
// ============================================================

/** Valeur ronde (1, 2 ou 5 fois une puissance de dix) au moins égale à `value`. */
export function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(value));
  // Tolérance : 0.1 / 0.1 ne vaut pas toujours 1 en virgule flottante.
  const unit = value / pow - 1e-9;
  const nice = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10;
  return nice * pow;
}

/**
 * Graduations rondes de 0 à un maximum rond qui contient `max` : environ `count` intervalles.
 * [0, 10, 20, 30, 40, 50] pour 50 ; [0, 0.5, 1, 1.5, 2] pour 1.8.
 */
export function niceTicks(max: number, count = 4): number[] {
  const top = Number.isFinite(max) && max > 0 ? max : 1;
  const step = niceCeil(top / count);
  const out: number[] = [];
  // Arrondi à 1e-9 : 0.1 + 0.2 ne doit pas donner une graduation 0.30000000000000004.
  for (let v = 0; v < top + step - 1e-9; v += step) out.push(Math.round(v * 1e9) / 1e9);
  return out;
}

/** Décimales nécessaires pour écrire ces graduations sans les arrondir : 0 pour [0, 1, 2], 1 pour [0, 0.5, 1]. */
export function tickDecimals(ticks: number[]): number {
  for (let d = 0; d < 3; d++) {
    if (ticks.every((v) => Math.abs(v * 10 ** d - Math.round(v * 10 ** d)) < 1e-6)) return d;
  }
  return 3;
}

/** Position d'une valeur sur un axe de `size` pixels, de `min` à `max`. */
export function scale(value: number, min: number, max: number, size: number): number {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * size;
}

export interface XY {
  x: number;
  y: number;
}

/**
 * Tronçons continus d'une série : un point sans valeur, ou un écart de plus d'un pas et demi entre
 * deux points, coupe le tracé. Chaque tronçon se dessine d'un trait ; un tronçon d'un seul point se
 * dessine d'un point.
 */
export function runs<T extends { t: number }>(points: T[], value: (p: T) => number | null, stepMs: number): T[][] {
  const out: T[][] = [];
  let current: T[] = [];
  let previous: T | null = null;
  for (const p of points) {
    const v = value(p);
    if (v == null || !Number.isFinite(v)) {
      if (current.length) out.push(current);
      current = [];
      previous = null;
      continue;
    }
    if (previous && p.t - previous.t > stepMs * 1.5) {
      out.push(current);
      current = [];
    }
    current.push(p);
    previous = p;
  }
  if (current.length) out.push(current);
  return out;
}

/** Tracé SVG d'un tronçon (« M x y L x y ... »), coordonnées arrondies au dixième de pixel. */
export function linePath(points: XY[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${round(p.x)} ${round(p.y)}`).join(' ');
}

/** Surface sous un tronçon, refermée sur la ligne de base `baseY`. */
export function areaPath(points: XY[], baseY: number): string {
  if (points.length === 0) return '';
  const first = points[0];
  const last = points[points.length - 1];
  return `${linePath(points)} L${round(last.x)} ${round(baseY)} L${round(first.x)} ${round(baseY)} Z`;
}

function round(v: number): number {
  return Math.round(v * 10) / 10;
}

/**
 * Colonne à tête arrondie et pied carré (la tête est le bout qui porte la donnée), tracée depuis la
 * ligne de base. Le rayon se réduit quand la colonne est plus basse que lui.
 */
export function columnPath(x: number, width: number, top: number, baseY: number, radius = 4): string {
  const h = Math.max(0, baseY - top);
  const r = Math.min(radius, width / 2, h);
  const x2 = x + width;
  return `M${round(x)} ${round(baseY)} L${round(x)} ${round(top + r)} Q${round(x)} ${round(top)} ${round(x + r)} ${round(top)} `
    + `L${round(x2 - r)} ${round(top)} Q${round(x2)} ${round(top)} ${round(x2)} ${round(top + r)} L${round(x2)} ${round(baseY)} Z`;
}

/**
 * De quel côté écrire le nom d'un seuil pour qu'il ne chevauche pas la courbe : le côté où les mesures
 * sont le plus loin du seuil. On compare le premier et le dernier cinquième des mesures.
 */
export function labelSide(values: (number | null)[], threshold: number): 'left' | 'right' {
  const known = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (known.length === 0) return 'right';
  const n = Math.max(1, Math.round(known.length / 5));
  const gap = (part: number[]) => Math.min(...part.map((v) => Math.abs(v - threshold)));
  return gap(known.slice(0, n)) > gap(known.slice(-n)) ? 'left' : 'right';
}

/** Point le plus proche d'une abscisse (le réticule se cale sur une mesure, pas entre deux). */
export function nearest<T extends { t: number }>(points: T[], t: number): T | null {
  let best: T | null = null;
  let bestGap = Infinity;
  for (const p of points) {
    const gap = Math.abs(p.t - t);
    if (gap < bestGap) {
      best = p;
      bestGap = gap;
    }
  }
  return best;
}

export interface TimeTick {
  t: number;
  /** Graduation de minuit : la page y écrit le jour en plus de l'heure. */
  midnight: boolean;
}

/** Pas des graduations de l'axe du temps, en minutes, selon la largeur de la période. */
export function timeStepMinutes(spanMs: number): number {
  const hours = spanMs / 3_600_000;
  if (hours <= 1.5) return 10;
  if (hours <= 8) return 60;
  if (hours <= 30) return 240;
  return 1440;
}

/**
 * Graduations de l'axe du temps, calées sur l'heure LOCALE : toutes les 10 minutes, toutes les
 * heures, toutes les 4 heures ou à minuit, selon la période.
 *
 * @param offsetMinutes décalage du fuseau local à un instant, en minutes à l'est de UTC (heure d'été
 *                      comprise) ; omis : celui du navigateur. Paramètre pour les tests.
 */
export function timeTicks(
  from: number, to: number,
  offsetMinutes: (t: number) => number = (t) => -new Date(t).getTimezoneOffset(),
): TimeTick[] {
  if (!(to > from)) return [];
  const stepMs = timeStepMinutes(to - from) * 60_000;
  const out: TimeTick[] = [];
  // On compte en « heure locale » : l'instant décalé du fuseau, découpé en pas ronds, puis ramené.
  const offset = offsetMinutes(from) * 60_000;
  let local = Math.ceil((from + offset) / stepMs) * stepMs;
  for (let guard = 0; guard < 400; guard++) {
    const t = local - offsetMinutes(local - offset) * 60_000;
    if (t > to) break;
    if (t >= from) out.push({ t, midnight: local % 86_400_000 === 0 });
    local += stepMs;
  }
  return out;
}
